import "./tile-fields.scss";

import * as React from "react";

import {cs} from "@common/react/chain-services";
import {consumeContext, provideContext} from "@common/react/context";
import {scope} from "@common/react/scope";
import {getPath} from "@common/utils/arr-path";
import {Expandable} from "../../../../../../../common/expandable/expandable";
import {getTileActionsUpdated} from "../actions/get-tile-actions-updated";
import {getTileFields} from "./common/get-tile-fields";
import {hasDateGroupsConfiguration} from "./common/has-date-groups-configuration";
import {getLimitUpdated} from "./common/limit-sorting/limit/get-limit-updated";
import {Limit} from "./common/limit-sorting/limit/limit";
import {AxisSorting} from "./common/limit-sorting/sorting/axis/axis-sorting";
import {getAxisSortingUpdated, getAxisSortingUpdatedByLimit} from "./common/limit-sorting/sorting/axis/get-axis-sorting-updated";
import {getGroupSortingUpdated} from "./common/limit-sorting/sorting/group/get-group-sorting-updated";
import {GroupSorting} from "./common/limit-sorting/sorting/group/group-sorting";
import {MeasureFieldGroups} from "./common/measure-field-groups/measure-field-groups";
import {getColumnType} from "./common/select-field/select-field";
import {SingleFieldWell} from "./common/single-field-well/single-field-well";
import {updateLimitSortingInTile, updateMeasureFieldsGroupsInTile, updateSingleFieldInTile} from "./common/update-tile";

export const LineChartFields = ({tile, isSubTile, tileActions, debugFunction, form, next}) =>
    cs(
        consumeContext("getFieldInfo"),
        [
            "tileFields",
            ({getFieldInfo}, next) =>
                next(
                    getTileFields({
                        tile: tile.value,
                        dimensionAttr: "xAxisField",
                        measureGroupAttrs: ["yAxisFields"],
                        groupFieldAttr: "groupField",
                        getFieldInfo,
                    })
                ),
        ],
        ({tileFields}, next) => provideContext("tileFields", tileFields, next),

        [
            "xAxisField",
            ({tileFields}, next) =>
                SingleFieldWell({
                    field: scope(tile, ["xAxisField"]),
                    onUpdateTile: (newVal, oriVal) =>
                        updateSingleFieldInTile({
                            fieldAttr: "xAxisField",
                            fieldVal: {newVal, oriVal},
                            tileFields,
                            tile,
                            updateSorting: getAxisSortingUpdated,
                            updateLimit: getLimitUpdated,
                            updateTileActions: getTileActionsUpdated,
                        }),
                    $type: "DimensionTileField",
                    label: "X-Axis",
                    next,
                }),
        ],
        [
            "yAxisFields",
            ({tileFields}, next) =>
                MeasureFieldGroups({
                    fieldGroups: scope(tile, ["yAxisFields"]),
                    onUpdateTile: (newVal, indexes) =>
                        updateMeasureFieldsGroupsInTile({
                            fieldAttr: "yAxisFields",
                            fieldVal: {newVal, oriVal: null},
                            tileFields,
                            indexes,
                            tile,
                            updateSorting: getAxisSortingUpdated,
                            updateLimit: getLimitUpdated,
                        }),
                    label: "Y-Axis",
                    info: (
                        <span>
                            The <b>OR</b> option will create a separate axis on the chart. You may control how multiple axes display in the Display tab, Multiple Axis Display
                            option.
                        </span>
                    ),
                    direction: "top",
                    next,
                }),
        ],
        [
            "groupField",
            ({tileFields}, next) =>
                SingleFieldWell({
                    field: scope(tile, ["groupField"]),
                    hasDateGroupsConfiguration: hasDateGroupsConfiguration(tile.value.$type),
                    $type: "CategoryTileField",
                    label: "Group",
                    filterColumn: (column) => column.$type !== "AggregatedMeasureModelColumn",
                    onUpdateTile: (newVal, oriVal) =>
                        updateSingleFieldInTile({
                            fieldAttr: "groupField",
                            fieldVal: {newVal, oriVal},
                            tileFields,
                            tile,
                            updateSorting: getGroupSortingUpdated,
                            updateTileActions: getTileActionsUpdated,
                        }),
                    next,
                }),
        ],
        [
            "fieldsSection",
            ({xAxisField, yAxisFields, groupField}, next) =>
                next({
                    render: () =>
                        Expandable({
                            label: "Fields",
                            render: () => (
                                <>
                                    {xAxisField.render()}
                                    {yAxisFields.render()}
                                    {groupField.render()}
                                </>
                            ),
                        }),
                    override: xAxisField.override || yAxisFields.override || groupField.override,
                }),
        ],

        [
            "limitSorting",
            ({tileFields}, next) =>
                cs(
                    [
                        "limit",
                        (_, next) => {
                            const formField = form.field(["limit"]);
                            return next(
                                Limit({
                                    allFields: tileFields.allFieldsWithoutUniq,
                                    field: formField.state,
                                    onChange: (newLimit, field) => {
                                        const limit = {
                                            ...newLimit,
                                            direction: field.$type === "MeasureTileField" ? "Asc" : newLimit.direction,
                                        };

                                        updateLimitSortingInTile({
                                            tile,
                                            limit,
                                            updateSorting: () =>
                                                getAxisSortingUpdatedByLimit({
                                                    tile,
                                                    field: {
                                                        ...limit,
                                                        $type: field.$type,
                                                    },
                                                    dimensionFieldAttr: "xAxisField",
                                                }),
                                        });
                                    },
                                    isHiddenRankOption: () => getPath(formField.state.value, ["modelColumnID"]) == getPath(tile.value, ["xAxisField", "modelColumnID"]),
                                })
                            );
                        },
                    ],
                    [
                        "sorting",
                        (_, next) =>
                            next(
                                <>
                                    {AxisSorting({
                                        fieldGroups: form.field(["sort"]).state,
                                        title: "X-Axis",
                                    })}
                                    {tile.value.groupField && GroupSorting({form, tile})}
                                </>
                            ),
                    ],
                    ({limit, sorting}) =>
                        next({
                            render: () =>
                                Expandable({
                                    initExpand: false,
                                    label: "Limit & Sorting",
                                    render: () => (
                                        <>
                                            {limit}
                                            {sorting}
                                        </>
                                    ),
                                }),
                        })
                ),
        ],

        ({fieldsSection, limitSorting}) =>
            next({
                render: () => (
                    <div className="tile-fields-3vr">
                        {fieldsSection.render()}
                        {limitSorting.render()}
                        {tileActions?.render?.()}
                        {isSubTile && debugFunction?.render?.({})}
                    </div>
                ),
                override: fieldsSection.override || debugFunction?.override() || tileActions?.override({fieldType: "MeasureTileField"}),
                overrideAll: debugFunction?.overrideAll || tileActions?.overrideAll,
            })
    );
